import MakedoConfig from './_config.js';
import { MakedoError, ErrorCodes, fromHttpError } from './errors.js';

// Lazy URL builders — values resolved at call time, not import time
const URLS = {
  get general_crud()  { return MakedoConfig.apiUrlBase + 'api/session/crud/sync'; },
  get general_get()   { return MakedoConfig.apiUrlBase + 'api/session/data/sync'; },
  get auth()          { return MakedoConfig.apiUrlBase + 'api/session/auth/sync'; },
  get basicActions()  { return MakedoConfig.apiUrlBase + 'api/session/basic/sync'; },
  get logUrl()        { return MakedoConfig.apiUrlBase + 'api/session/logs/sync'; },
  get bactivity()     { return MakedoConfig.apiUrlBase + 'api/session/bactivity.jsp'; },
  get authUsers()     { return MakedoConfig.apiUrlBase + 'api/session/authUsers.jsp'; },  // user creation + token refresh
  get fileUpload()    { return MakedoConfig.apiUrlBase + 'api/session/fileUpload.jsp'; }   // multipart media upload (audio, future headshot)
};

const Fetch = { // consider where to use: postUpdateAsJsonBeacon() from api_fetchers.js
  
  // Auth token for cross-domain requests (set after login)
  authToken: null,

  // User ID of the currently logged-in user (set after login, used for token refresh)
  userId: null,
  
  // Context ID for scoping all requests to a developer's app context (set once at page init)
  contextId: null,

  // Project key for non-proxy authorization (set once at page init; sent as X-Project-Auth-Token header)
  projectKey: null,

  // Proxy URL for proxy-mode projects (set once at page init; used to route token refresh through developer's proxy)
  proxyUrl: null,

  // Opt-in feature flags from init({ features: {...} }) — e.g. { transcription: true }.
  // Gates client capabilities such as the lazy-loaded session recorder.
  features: null,
  
  setAuthToken(token) {
    // Guard: never store obviously invalid tokens
    if (token && (typeof token !== 'string' || token.length < 8 || token === 'null' || token === 'undefined')) {
      console.error('setAuthToken() called with invalid token — ignoring:', token);
      return;
    }
    this.authToken = token;
    // Also expose on window for non-module scripts (e.g., bactivity.js)
    if (typeof window !== 'undefined') window.__makedoAuthToken = token;
    // Persist token as cookie so it survives page refreshes
    if (typeof document !== 'undefined' && token) {
      document.cookie = `vibelive_auth=${token}; path=/; max-age=86400; SameSite=Lax; Secure`;
    }
    console.debug('🔐 Auth token set:', token ? '***' + token.slice(-4) : 'null');
  },
  
  clearAuthToken() {
    this.authToken = null;
    if (typeof window !== 'undefined') window.__makedoAuthToken = null;
    // Expire the auth cookie
    if (typeof document !== 'undefined') {
      document.cookie = 'vibelive_auth=; path=/; max-age=0; SameSite=Lax; Secure';
    }
    console.debug('🔓 Auth token cleared');
  },

  // Restore auth token from cookie (call on page load to rehydrate session)
  restoreAuthToken() {
    if (typeof document === 'undefined') return false;
    const match = document.cookie.match(/(?:^|;\s*)vibelive_auth=([^;]+)/);
    if (match) {
      this.setAuthToken(match[1]);
      console.debug('🔄 Auth token restored from cookie');
      return true;
    }
    return false;
  },
  
  setUserId(id) {
    this.userId = id;
  },

  clearUserId() {
    this.userId = null;
  },

  // Context ID management — set once at app init, auto-attached to every request
  setContextId(id) {
    this.contextId = id;
  },
  
  clearContextId() {
    this.contextId = null;
  },

  setProjectKey(key) {
    this.projectKey = key;
  },

  clearProjectKey() {
    this.projectKey = null;
  },

  setProxyUrl(url) {
    this.proxyUrl = url;
  },

  clearProxyUrl() {
    this.proxyUrl = null;
  },

  // Feature flags — set once at app init from init({ features: {...} })
  setFeatures(features) {
    this.features = features;
  },

  /**
   * Refresh the current user's auth token.
   * If a proxyUrl is set (proxy mode), routes through the developer's proxy so it can
   * add the contextAuthToken server-side — required for non-trial projects.
   * Otherwise calls authUsers.jsp directly (works for trial and projectKey projects).
   * On success, the new token is automatically stored via handleAuthResult().
   * @returns {Promise<Object>} { id, temp_auth_token, status, ... }
   */
  refreshAuthToken: async () => {
    if (!Fetch.userId || !Fetch.authToken) {
      throw new MakedoError(ErrorCodes.AUTH_TOKEN_REFRESH_FAILED, 'Cannot refresh token: no userId or authToken stored', { context: 'refreshAuthToken', retriable: false });
    }
    // Proxy mode: route through developer's proxy so contextAuthToken is added server-side.
    // Direct call to authUsers.jsp would fail with 403 for non-trial projects — the proxy
    // is the only thing that can satisfy rx.opsContext for this project type.
    if (Fetch.proxyUrl) {
      const result = await Fetch.authorizeViaProxy(Fetch.proxyUrl, {
        userId  : Fetch.userId,
        // authToken is auto-attached by authorizeViaProxy
        // contextId is auto-attached by authorizeViaProxy
      });
      if (result.status !== 'loggedIn') {
        throw new MakedoError(ErrorCodes.AUTH_TOKEN_REFRESH_FAILED, 'Proxy token refresh failed: ' + (result.error || result.note || result.status), { context: 'refreshAuthToken', retriable: false });
      }
      return result;
    }
    // Non-proxy path (trial or projectKey): call authUsers.jsp directly.
    const data = {
      action       : 'refreshAuthToken',
      userId       : Fetch.userId,
      userAuthToken: Fetch.authToken,
      // contextId is auto-injected by requestData()
    };
    const result = await Fetch.requestData(URLS.authUsers, { method: 'post', data, _skipRefresh: true });
    return Fetch.handleAuthResult(result);
  },
  
  // Helper to handle auth result and set token
  handleAuthResult(result) {
    if (result.status === 'loggedIn' && result.temp_auth_token) {
      // Reject obviously invalid token values (e.g. server rendered Java null as the string "null")
      if (typeof result.temp_auth_token !== 'string' || result.temp_auth_token.length < 8 || result.temp_auth_token === 'null' || result.temp_auth_token === 'undefined') {
        console.error('Auth result contained invalid token — treating as auth failure');
        result.status = 'failed';
        result.error = 'Server returned an invalid auth token';
        return result;
      }
      Fetch.setAuthToken(result.temp_auth_token);
    }
    return result;
  },

  /**
   * Authorize a user via a developer-provided proxy.
   * The proxy adds contextAuthToken server-side and forwards to authorizeUser.jsp.
   * Works for both new user creation and existing user token refresh.
   * @param {string} proxyUrl - The developer's proxy URL
   * @param {Object} payload - { contextId, userId?, username?, minutes? }
   * @returns {Promise<Object>} { id, pid, username, temp_auth_token, expiresInMinutes, status }
   */
  authorizeViaProxy: async (proxyUrl, payload) => {
    // Attach authToken and contextId to proxy payload if available
    if (Fetch.authToken) { payload.authToken = Fetch.authToken; }
    if (Fetch.contextId) { payload.contextId = Fetch.contextId; }
    
    const response = await fetch(proxyUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    if (!response.ok) {
      throw new MakedoError(ErrorCodes.AUTH_PROXY_FAILED, `Proxy request failed: ${response.status} ${response.statusText}`, { context: 'authorizeViaProxy', retriable: true });
    }
    const result = await response.json();
    return Fetch.handleAuthResult(result);
  },
  
  requestData: async (url, { format = 'json', method = 'GET', data = {}, _skipRefresh = false } = {}) => {
    // Add authToken to data if present (for cross-domain requests)
    if (Fetch.authToken) {
      data.authToken = Fetch.authToken;
    }
    // Add contextId to data if present (scopes request to developer's app context)
    if (Fetch.contextId) {
      data.contextId = Fetch.contextId;
    }
    
    const is_get = (method.toUpperCase() === 'GET');
    const headers = {};
    if (!is_get) headers['Content-Type'] = 'application/json';
    if (Fetch.projectKey) headers['X-Project-Auth-Token'] = Fetch.projectKey;
    const config = {
      method: method.toUpperCase(),
      ...(Object.keys(headers).length > 0 && { headers }),
      ...(!is_get && { body: JSON.stringify(data) }),
    };
    if(is_get){
      const queryParams = new URLSearchParams(data);
      url = `${url}?${queryParams.toString()}`;
    }
    
    try {
      const response = await fetch(url, config);
      
      if (response.status === 401 && !_skipRefresh && Fetch.userId && Fetch.authToken) {
        console.warn('🔄 401 received — attempting token refresh...');
        try {
          const tokenBefore = Fetch.authToken;
          await Fetch.refreshAuthToken();
          if (Fetch.authToken === tokenBefore) {
            throw new Error('Token refresh produced no new token');
          }
          console.warn('🔄 Token refreshed — retrying original request...');
          return Fetch.requestData(url, { format, method, data, _skipRefresh: true });
        } catch (refreshError) {
          throw new MakedoError(ErrorCodes.AUTH_TOKEN_REFRESH_FAILED, 'Session expired and token refresh failed. Please log in again.', { cause: refreshError, context: 'requestData', retriable: false });
        }
      }

      if (!response.ok) {
        let errMsg = `HTTP error! Status: ${response.status}`;
        let errBody = null;
        try {
          errBody = await response.json();
          if (errBody.error) errMsg = errBody.error;
        } catch (_) {}
        const mErr = fromHttpError(response.status, errMsg, 'requestData', errBody);
        if (errBody) mErr.serverBody = errBody;
        throw mErr;
      }
      
      const result = format.toLowerCase() === 'json' ? await response.json() : await response.text();
      return result;
    } catch (error) {
      // Wrap unexpected network failures (e.g. fetch itself threw) that aren't already MakedoError
      if (error instanceof MakedoError) throw error;
      throw fromHttpError(0, error.message || 'Network request failed', 'requestData');
    }
  },
  
login : async ({email, password}) => {
    const data = {
      action      : 'login',
      type        : 'user',
      email    : email,
      password    : password,
    }
    // contextId is auto-injected by requestData()
    
    const result = await Fetch.requestData(URLS.auth, {method:'post', data:data});
    return Fetch.handleAuthResult(result);
  },
  signup : async ({email, password}) => {
    const data = {
      action      : 'signup',
      type        : 'user',
      email    : email,
      password    : password,
    }
    const result = await Fetch.requestData(URLS.auth, {method:'post', data:data});
    return Fetch.handleAuthResult(result);
  },
  signupInContext : async ({email, password, contextAuthToken}) => {
    const data = {
      action      : 'signupInContext',
      type        : 'user',
      // contextId is auto-injected by requestData()
      contextAuthToken : contextAuthToken,
      email    : email,
      password    : password,
    }
    const result = await Fetch.requestData(URLS.auth, {method:'post', data:data});
    return Fetch.handleAuthResult(result);
  },
  signupAnonInContext : async ({password, username}) => {
    const data = {
      action      : 'makeUserInContext',
      type        : 'user',
      // contextId is auto-injected by requestData()
      // contextAuthToken is injected server-side by the developer's proxy for non-trial projects
      username    : username,
      password    : password,
    }
    const result = await Fetch.requestData(URLS.authUsers, {method:'post', data:data});
    return Fetch.handleAuthResult(result);
  },
  logout : async ({}) => {
    const data = {
      action      : 'logout',
    }
    const result = await Fetch.requestData(URLS.auth, {method:'post', data:data});
    
    // Clear authToken on logout
    if (result.status === 'loggedOut') {
      Fetch.clearAuthToken();
    }
    
    return result;
  },

    getOpenActiveInvites : async ({count = 10, depth = 1, sortField = 'uts', sortOrder = 'desc'}) => {
      const data = {
                      type      : 'invite',
                      kind      : 'channel',
                      depth     : depth,
                      count     : count,
                      //tag      : '*pinned',
                      sortField : sortField,
                      sortOrder : sortOrder
                    }
      return await Fetch.requestData(URLS.general_get, {method:'post', data});
    },

    getMyChannels : async ({count = 10, depth = 1, sortField = 'uts', sortOrder = 'desc'}) => {
      const data = {
                      type      : 'history',
                      kind      : 'channel',
                      depth     : depth,
                      count     : count,
                      //tag      : '*pinned',
                      sortField : sortField,
                      sortOrder : sortOrder
                    }
      return await Fetch.requestData(URLS.general_get, {method:'post', data});
    },

    getSelf : async({depth = 2}) => {
      let json = await Fetch.requestData(URLS.general_get, {method:'post', data:{type:'self', depth:depth}});
      if (json?.error_code) console.warn('[Fetch.getSelf] server error:', json);
      return json;
    },
  
    getOneUser : async({user_id, depth = 1}) => {
      if (!user_id) return;
      let json = await Fetch.requestData(URLS.general_get, {method:'post', data:{type:'user', id:user_id, depth:depth}});
      return json;
    },
    
    getIntervalsByUser : async({user_id, limit = 10}) => {
      if (!user_id) return;
      let json = await Fetch.requestData(URLS.general_get, {method:'post', data:{type:'interval', user:user_id, limit:limit}});
      return json; 
    },
    getIntervalsByTopic : async({topic_label, limit = 10}) => {
      if (!topic_label) return;
      let json = await Fetch.requestData(URLS.general_get, {method:'post', data:{type:'interval', topicLabel:topic_label, limit:limit}});
      return json; 
    },
    getIntervalsByChannel : async({channel_id, limit = 10}) => {
      if (!channel_id) return;
      let json = await Fetch.requestData(URLS.general_get, {method:'post', data:{type:'interval', channel:channel_id, limit:limit}});
      return json; 
    },
  
  getOneMember : async ({member_id, depth=1}) => {
    const data = {
                    type  : 'member',
                    id    : member_id,
                    depth : depth
                  }
    return await Fetch.requestData(URLS.general_get, {method:'post', data});
  },
  
    // POST
    getOrCreateSelfMember : async({channel_id, display_name}) => {
      if (!channel_id) {
        throw new MakedoError(ErrorCodes.INVALID_PARAM, 'getOrCreateSelfMember requires a channel_id.', { context: 'getOrCreateSelfMember' });
      }
      const data= {
        type:'member', 
        action:'get_create', 
        channel:channel_id
      }
      if (display_name) { data.display_name = display_name; }
      const result = await Fetch.requestData(URLS.general_crud, {method:'post', data:data});
      if (result && result.error) {
        // Server returned 200 with an error body — use fromHttpError with serverBody
        // to get the same granular mapping (room_full, room_disabled, context_mismatch, etc.)
        const mErr = fromHttpError(200, result.error, 'getOrCreateSelfMember', result);
        // If fromHttpError didn't match a server status, fall back to ROOM_JOIN_FAILED
        if (mErr.code === ErrorCodes.NETWORK_HTTP_ERROR) {
          mErr.code = ErrorCodes.ROOM_JOIN_FAILED;
        }
        mErr.serverBody = result;
        throw mErr;
      }
      return result;
    },
    updateUser : async ({user_id, config = {}}) => {
      const data = {
        action      : 'update_config',
        type        : 'user',
        id          : user_id,
        data: {
          userId   : user_id,
          ...config
        }
      }
      return await Fetch.requestData(URLS.general_crud, {method:'post', data:data});
    },
    updateSelf : async ({user_id, config = {}}) => {
      const data = {
        action      : 'update_config',
        type        : 'self',
        id          : user_id,
        data: {
          userId   : user_id,
          ...config
        }
      }
      return await Fetch.requestData(URLS.general_crud, {method:'post', data:data});
    },
  
      // POST
  updateSelfTagInfo : async ({tagtreeAction='add', tag_info = ''}) => {  // add, set, clear
    const data = {
      action      : 'update_tag_info',
      type        : 'self',
      data        : {
        tagtreeAction,
        tag_info
      }
    }
    return await Fetch.requestData(URLS.general_crud, {method:'post', data:data});
  },
  updateUserTagInfo : async ({user_id, tagtreeAction='add', tag_info = ''}) => {  // add, set, clear
    const data = {
      action      : 'update_tag_info',
      type        : 'user',
      id          : user_id,
      data        : {
        tagtreeAction,
        tag_info
      }
    }
    return await Fetch.requestData(URLS.general_crud, {method:'post', data:data});
  },
  updateChannelTagInfo : async ({channel_id, tagtreeAction='add', tag_info = ''}) => {  // add, set, clear
    const data = {
      action      : 'update_tag_info',
      type        : 'channel',
      id          : channel_id,
      data        : {
        tagtreeAction,
        tag_info
      }
    }
    return await Fetch.requestData(URLS.general_crud, {method:'post', data:data});
  },
    tagChannel : async ({channel_id, tag}) => {
      return await Fetch.updateChannelTagInfo({
        channel_id: channel_id,
        tagtreeAction: 'add',
        tag_info: tag
      });
    },
    tagUser : async ({user_id, tag}) => {
      return await Fetch.updateUserTagInfo({
        user_id: user_id,
        tagtreeAction: 'add',
        tag_info: tag
      });
    },
  
    pinUser : async ({user_id}) => {
      return await Fetch.updateUserTagInfo({
        user_id: user_id,
        tagtreeAction: 'set',
        tag_info: 'pinned:true'
      });
    },
    unpinUser : async ({user_id}) => {
      return await Fetch.updateUserTagInfo({
        user_id: user_id,
        tagtreeAction: 'set',
        tag_info: 'pinned:false'
      });
    },
  
    getOneChannel : async ({channel_id, room_code, depth = 1}) => {
    const data = {
                  type  : 'channel',
                  depth : depth
                };
    // Only include id or room_code if provided (not undefined)
    if (channel_id !== undefined) data.id = channel_id;
    if (room_code !== undefined) data.room_code = room_code;
    
    const result = await Fetch.requestData(URLS.general_get, {method:'post', data});
    // Server returns 200 with {error: "..."} for invalid/forbidden lookups
    if (result && result.error) {
      throw new MakedoError(ErrorCodes.ROOM_NOT_FOUND, result.error, {
        context: 'getOneChannel',
        retriable: false
      });
    }
    return result;
  },


  getMembers : async ({channel_id, depth = 1}) => {
    const data = {
                  type  : 'member',
                  channel    : channel_id,
                  depth : depth
                }
    return await Fetch.requestData(URLS.general_get, {method:'post', data});
  },


  getUsersByQuery : async ({query='', count=10, depth=1} = {}) => {
    const data = {
      q:query, 
      type:'user', count, depth
    };
    return await Fetch.requestData(URLS.general_get, {method:'post', data});
  },

  // POST
  createQuickChatChannel : async ({invited, message = ''}) => {
    const data = {
        action        : 'create',
        type          : 'quickchat',
        data          : {
          invited,
          message,
        },
    }
    return await Fetch.requestData(URLS.general_crud, {method:'post', data:data});
  },
  // POST
  createNewChannel : async ({title, description, tone, max_size, allows_guests = true, display_name, statusOnMeetingEnd = null, maxLength = null, enableTextChat = null, allowScreensharing = null, allowLurkers = null, useEmailWhitelist = null, allowFileSharing = null, transcriptionActive = null}) => {
    const data = {
        action        : 'create',
        type          : 'channel',
        data: {
          title, 
          description,
          tone,
          allows_guests:`${allows_guests}`,
        },
    }
    // max_size only sent when explicitly provided (else server default of 8 applies)
    if (max_size != null) {
      data.data.max_size = `${max_size}`;
    }
    // Add display_name if provided (for room creator's member display name)
    if (display_name) {
      data.data.display_name = display_name;
    }
    // Add statusOnMeetingEnd if provided (room-level config override)
    if (statusOnMeetingEnd) {
      data.data.statusOnMeetingEnd = statusOnMeetingEnd;
    }
    // Room config flags — only sent when explicitly set (absent = inherit project/account default)
    if (maxLength != null)          { data.data.maxLength         = `${maxLength}`; }
    if (enableTextChat != null)     { data.data.enableTextChat     = enableTextChat; }
    if (allowScreensharing != null) { data.data.allowScreensharing = allowScreensharing; }
    if (allowLurkers != null)       { data.data.allowLurkers       = allowLurkers; }
    if (useEmailWhitelist != null)  { data.data.useEmailWhitelist  = useEmailWhitelist; }
    if (allowFileSharing != null)    { data.data.allowFileSharing   = allowFileSharing; }
    // transcriptionActive turns recording on for this room; only takes effect when the project's
    // transcriptionAllowed capability is also true. Absent = inherit project/room cascade default.
    if (transcriptionActive != null) { data.data.transcriptionActive = transcriptionActive; }
    // contextId is auto-injected by requestData()
    return await Fetch.requestData(URLS.general_crud, {method:'post', data:data});
  },


  // POST
  sendInvite : async ({channel_id, user_id, message = '', ttlSeconds = 0}) => {
    const data = {
      action      : 'send_invite',
      type        : 'channel',
      id          : channel_id,
      data: {
        user_id      : user_id,
        message      : message,
        ttlSeconds   : String(ttlSeconds),
      }
    }
    return await Fetch.requestData(URLS.general_crud, {method:'post', data:data});
  },


  // POST
  addUserToChannel : async ({channel_id, user_id}) => {
    const data = {
      action      : 'add_member',
      type        : 'channel',
      id          : channel_id,
      data: {
        userId      : user_id,
      }
    }
    return await Fetch.requestData(URLS.general_crud, {method:'post', data:data});
  },
  
  // POST
  addEmailListToChannel : async ({channel_id, email_list}) => {
    const data = {
      action      : 'add_email_list',
      type        : 'channel',
      id          : channel_id,
      data: {
        email_list  : email_list,
      }
    }
    return await Fetch.requestData(URLS.general_crud, {method:'post', data:data});
  },

  // POST

  // POST
  updateChannel : async ({channel_id, config = {}}) => {
    const data = {
      action      : 'update_config',
      type        : 'channel',
      id          : channel_id,
      data: {
        ...config
      }
    }
    return await Fetch.requestData(URLS.general_crud, {method:'post', data:data});
  },
  updateMember : async ({member_id, config = {}}) => {
    const data = {
      action      : 'update_config',
      type        : 'member',
      id          : member_id,
      data: {
        ...config
      }
    }
    return await Fetch.requestData(URLS.general_crud, {method:'post', data});
  },

  // POST — owner-writable app metadata blob on a channel (opaque JSON string; null/'' clears)
  setChannelMetadata : async ({channel_id, metadata}) => {
    const data = {
      action      : 'set_metadata',
      type        : 'channel',
      id          : channel_id,
      data        : { metadata: metadata ?? '' }
    }
    return await Fetch.requestData(URLS.general_crud, {method:'post', data});
  },

  // POST — owner-initiated removal of a member from their channel
  kickMember : async ({member_id, message = null}) => {
    const data = {
      action      : 'kick',
      type        : 'member',
      id          : member_id,
      data        : message ? { message } : {}
    }
    return await Fetch.requestData(URLS.general_crud, {method:'post', data});
  },


  // POST
  declineInvite : async ({member_id}) => {
    const data = {
      action      : 'decline_invite',
      type        : 'member',
      id          : member_id,
      data: {}
    }
    return await Fetch.requestData(URLS.general_crud, {method:'post', data});
  },

  // POST — accept an invite; server flips status, returns channel info incl. room_code
  acceptInvite : async ({member_id}) => {
    const data = {
      action : 'accept_invite',
      type   : 'member',
      id     : member_id,
      data   : {}
    };
    return await Fetch.requestData(URLS.general_crud, {method:'post', data});
  },

  // POST — set availability (transient, server-memory only, project scoped)
  setAvailable : async ({open = true, ttlSeconds = 3600, useProfile = '', seeking = '',
                         role = '', attributes = null} = {}) => {
    const payload = { open: String(open), ttlSeconds: String(ttlSeconds), useProfile, seeking };
    if (role) payload.role = role;
    if (attributes && typeof attributes === 'object') {
      // crud.jsp's dataMap is Map<String,String>; serialize attributes once.
      payload.attributes = JSON.stringify(attributes);
    }
    const data = { action : 'set_available', type : 'user', data : payload };
    return await Fetch.requestData(URLS.general_crud, {method:'post', data});
  },

  // POST — list users currently available in the caller's project
  getAvailableUsers : async ({count = 20} = {}) => {
    const data = {
      action : 'get_available',
      type   : 'user',
      data   : { count: String(count) }
    };
    return await Fetch.requestData(URLS.general_crud, {method:'post', data});
  },

  /**
   * Fetch recent messages for channel or member conversation
   * @param {Object} options - Message fetch options
   * @param {string} options.channelId - Channel ID (required)
   * @param {string} [options.memberId] - Member ID for private messages (optional)
   * @param {number} [options.count=10] - Number of messages to fetch
   * @param {number} [options.page=0] - Page number (0-based)
   * @param {number} [options.depth=1] - API depth parameter
   * @returns {Promise<Array>} Array of message objects
   */
  getMessages: async ({channelId, memberId = null, count = 10, page = 0, depth = 1}) => {
    const data = {
      type: 'message',
      count: count,
      page: page,
      depth: depth
    };

    if (memberId) {
      // Member-specific messages
      data.member = memberId;
      data.channel = channelId; // Include channel context for member messages
    } else {
      // Channel messages
      data.channel = channelId;
    }

    return await Fetch.requestData(URLS.general_get, {method:'post', data});
  },
  sendMessage: async ({channelId, memberId = null, content}) => {
    const target_type = memberId ? 'member' : 'channel';
    const target_id   = memberId ? memberId : channelId;
    const data = {
      action       : 'create',
      type         : 'message',
      target_type  : target_type,
      target_id    : target_id,
      content_type : 'text',
      content      : content,
    };
    // Include channel context when targeting a member
    if (memberId && channelId) { data.channel_id = channelId; }
    return await Fetch.requestData(URLS.general_crud, {method:'post', data});
  },

  deleteMessage: async ({messageId}) => {
    return await Fetch.requestData(URLS.general_crud, {method:'post', data:{
      action : 'delete',
      type   : 'message',
      id     : messageId,
    }});
  },

  deleteTextHistory: async ({channelId, roomMessages = true, dmMessages = false}) => {
    return await Fetch.requestData(URLS.general_crud, {method:'post', data:{
      action        : 'delete-all',
      type          : 'message',
      channel       : channelId,
      room_messages : roomMessages,
      dm_messages   : dmMessages,
    }});
  },
  
  
    getTopicByLabel : async({topic_label, depth = 1}) => {
      if (!topic_label) return;
      let json = await Fetch.requestData(URLS.general_get, {method:'post', data:{type:'topic', topicLabel:topic_label, depth:depth}});
      return json;
    },
  
    getTopicHistory : async({topic_label}) => {
      if (!topic_label) return;
      let json = await Fetch.requestData(URLS.general_get, {method:'post', data:{type:'interval', topicLabel:topic_label}});
      return json; 
    },
  
    // POST - Create a new topic-based channel
    createTopicChannel : async ({topic_label, title, description, tone, max_size, allows_guests = true}) => {
      const data = {
          action        : 'create',
          type          : 'channel',
          data: {
            title, 
            description,
            tone,
            max_size:`${max_size}`,
            allows_guests:`${allows_guests}`,
            topic_label: topic_label
          },
      }
      return await Fetch.requestData(URLS.general_crud, {method:'post', data:data});
    },
  
    // POST - Pin/unpin a topic
    pinTopic : async ({topic_label, is_pinned}) => {
      const data = {
        action      : 'pin',
        type        : 'topic',
        topicLabel  : topic_label,
        data: {
          is_pinned: is_pinned
        }
      }
      return await Fetch.requestData(URLS.general_crud, {method:'post', data:data});
    },

    

  fetchTopicHistories : async ({count = 3, depth = 1, sortField = 'uts', sortOrder = 'desc'}) => {
    const data = {
                    type      : 'history',
                    kind      : 'topic',
                    depth     : depth,
                    count     : count,
                    sortField : sortField,
                    sortOrder : sortOrder
                  }
    return await Fetch.requestData(URLS.general_get, {method:'post', data});
  },
  fetchGroupHistories : async ({count = 3, depth = 1, sortField = 'uts', sortOrder = 'desc'}) => {
    const data = {
                    type      : 'history',
                    kind      : 'group',
                    depth     : depth,
                    count     : count,
                    sortField : sortField,
                    sortOrder : sortOrder
                  }
    return await Fetch.requestData(URLS.general_get, {method:'post', data});
  },


  fetchUserHistories : async ({count = 3, depth = 1, sortField = 'uts', sortOrder = 'desc'}) => {
    const data = {
                    type      : 'history',
                    kind      : 'user',
                    depth     : depth,
                    count     : count,
                    sortField : sortField,
                    sortOrder : sortOrder
                  }
    return await Fetch.requestData(URLS.general_get, {method:'post', data});
  },
  
    // GET - Search topics
    searchTopics : async ({query = '', count = 10, depth = 1}) => {
      const data = {
        q: query,
        type: 'topic',
        count: count,
        depth: depth
      };
      return await Fetch.requestData(URLS.general_get, {method:'post', data});
    },
  
    // GET - Get popular topics
    getPopularTopics : async ({count = 10, depth = 1}) => {
      const data = {
        type: 'topic',
        kind: 'popular',
        count: count,
        depth: depth
      };
      return await Fetch.requestData(URLS.general_get, {method:'post', data});
    },
  
    // GET - Get trending topics
    getTrendingTopics : async ({count = 10, depth = 1}) => {
      const data = {
        type: 'topic',
        kind: 'trending',
        count: count,
        depth: depth
      };
      return await Fetch.requestData(URLS.general_get, {method:'post', data});
    },

      fetchActivity : async ({depth = 1} = {}) => {
        const data = {
                        type      : 'group',
                        depth     : depth,
                      }
        return await Fetch.requestData(URLS.general_get, {method:'post', data});
      },

    getInvites: async({}) => {
      try {
        /*
        if (this.invites) return this.invites;
        if (!this.profile) await this.init;
        const invites = await fetch(`api/session/get.jsp?type=invite&user=${this.profile.pid}&depth=1`);
        if (!invites.ok) throw new Error('Network error on getMeUser request');
        this.invites = await invites.json();
        return this.invites;
        */
       return [];
      } catch (error) {
        console.error('Error fetching invitations:', error);
        return [];
      }
    },

// from api_fetchers.js
    
 delog(actor_type,actor_pid, action, notes_obj) { // used by streams.js
  const apiUrl = URLS.logUrl;
  const data = {actor_type: actor_type, actor_pid: actor_pid, action: action, notes_obj: notes_obj};
  Fetch.postUpdateAsJsonBeacon(apiUrl, data);
  //console.log("api_fetchers.jsp:evlog:sendbeacon() called with: "+actor_type+" "+actor_pid+" "+action+" "+notes_obj);
},
 setMemberStatus(memberId, newVal) { // used by streams.js, detectBrowserClose.js
  const apiUrl = URLS.basicActions;
  const data = {action: 'setMemberStatus', memberId: memberId,newVal: newVal};
  Fetch.postUpdateAsJsonBeacon(apiUrl, data);
},
 setMemberMedia(memberId, mediaType, audioState, videoState) { // used by streams.js
  const apiUrl = URLS.basicActions;
  const data = {action: 'setMemberMedia', memberId: memberId, mediaType: mediaType, audioState: audioState, videoState: videoState};
  Fetch.postUpdateAsJsonBeacon(apiUrl, data);
},
 recordBrowserActivity(channel_id, event_type, mediaStats = null) { // used by bactivity.js
  const apiUrl = URLS.bactivity;
  const data = {
    channel_id: channel_id,
    timestamp: new Date().toISOString(),
    event_type: event_type,
    ...(mediaStats && { media: mediaStats })
  };
  Fetch.postUpdateAsJsonBeacon(apiUrl, data);
},
 DBPing(obj_type, obj_pid) { // used by streams.js
  const apiUrl = URLS.basicActions;
  const data = {action: 'ping', type: obj_type, id: obj_pid};
  Fetch.postUpdateAsJsonBeacon(apiUrl, data);
},
 setLatestChannelId_db(newVal) { // used by Streams.js
  const apiUrl = URLS.basicActions;
  const data = {action: 'setLatestChannelIdParams',newVal: newVal};
  Fetch.postUpdateAsJsonBeacon(apiUrl, data);
},
 updateUsername(newVal) { // not used anywhere ???
  const apiUrl = URLS.basicActions;
  const data = {action: 'updateUsernameParams',newVal: newVal};
  Fetch.postUpdateAsJsonBeacon(apiUrl, data);
},
 updatePublicStatus(newVal) { // not used anywhere ???
  const apiUrl = URLS.basicActions;
  const data = {action: 'updatePublicStatusParams',newVal: newVal};
  Fetch.postUpdateAsJsonBeacon(apiUrl, data);
},
 setHomeChannelId(newVal) { // not used anywhere ???
  const apiUrl = URLS.basicActions;
  const data = {action: 'setHomeChannelIdParams',newVal: newVal};
  Fetch.postUpdateAsJsonBeacon(apiUrl, data);
},
 setLatestAsHomeChannelId() { // not used
  const apiUrl = URLS.basicActions;
  const data = {action: 'setLatestAsHomeChannelId'};
  Fetch.postUpdateAsJsonBeacon(apiUrl, data);
},
 setMemberProfilePic(member_id, newVal) { // not used anywhere ???
  const apiUrl = URLS.basicActions;
  const data = {action: 'setMemberProfilePic',member_id: member_id, newVal: newVal};
  Fetch.postUpdateAsJsonBeacon(apiUrl, data);
},
 startTranscribing(channel_id) { // not used anywhere ???
  //console.log("api_fetchers.jsp:startTranscribing called with: "+channel_id);
  const apiUrl = URLS.basicActions;
  const data = {action: 'startTranscribing',channel_id: channel_id};
  Fetch.postUpdateAsJsonBeacon(apiUrl, data);
},

/**
 * Upload a media blob via the multipart fileUpload endpoint.
 * Media-generic ('audio' now; 'headshot' later) so non-audio media reuses this spine.
 * @param {Object}  p
 * @param {string}  p.type       Upload discriminator: 'audio' (future: 'headshot').
 * @param {Blob}    p.blob       The media payload.
 * @param {Object}  [p.meta]     Extra form fields (e.g. { channel_id, utc_start_ts, utc_end_ts }).
 * @param {string}  [p.filename] Filename; its extension drives server-side type validation.
 * @returns {Promise<Object>}    Parsed JSON, e.g. { success, filename }.
 */
uploadMedia: async ({ type, blob, meta = {}, filename } = {}) => {
  if (!type || !blob) {
    throw new MakedoError(ErrorCodes.INVALID_PARAM, 'uploadMedia requires { type, blob }', { context: 'uploadMedia', retriable: false });
  }
  const form = new FormData();
  form.append('uploadType', type);
  form.append('file', blob, filename || `${type}.webm`);
  for (const [k, v] of Object.entries(meta)) {
    if (v !== undefined && v !== null) form.append(k, String(v));
  }
  if (Fetch.authToken) form.append('authToken', Fetch.authToken);
  if (Fetch.contextId) form.append('contextId', String(Fetch.contextId));

  const headers = {};
  if (Fetch.projectKey) headers['X-Project-Auth-Token'] = Fetch.projectKey;
  // NOTE: do NOT set Content-Type — the browser must add the multipart boundary.

  try {
    const response = await fetch(URLS.fileUpload, {
      method: 'POST',
      ...(Object.keys(headers).length > 0 && { headers }),
      body: form
    });
    if (!response.ok) {
      let errMsg = `HTTP error! Status: ${response.status}`;
      let errBody = null;
      try { errBody = await response.json(); if (errBody.error) errMsg = errBody.error; } catch (_) {}
      const mErr = fromHttpError(response.status, errMsg, 'uploadMedia', errBody);
      if (errBody) mErr.serverBody = errBody;
      throw mErr;
    }
    return await response.json();
  } catch (error) {
    if (error instanceof MakedoError) throw error;
    throw new MakedoError(ErrorCodes.AUDIO_UPLOAD_FAILED, error.message || 'Media upload failed', { cause: error, context: 'uploadMedia', retriable: true });
  }
},

/**
 * Retrieve the transcript bundle for a channel.
 * @param {string} channel_id
 * @returns {Promise<Object>} { channelId, status, text, utterances, summary?, tags? }
 */
getTranscript: async (channel_id) => {
  return await Fetch.requestData(URLS.basicActions, { method: 'post', data: { action: 'getTranscript', channel_id } });
},

/**
 * Retrieve the list of session summaries for a channel.
 * Each session corresponds to one member's continuous time in the room (internally an "interval").
 * @param {string} channel - channel public id
 * @param {Object} [options]
 * @param {number} [options.depth=1] - 1 for summary fields, 2 to also include msvp_details
 * @param {number} [options.count] - max number of sessions to return
 * @returns {Promise<Array>} list of session view objects (summary as headline field)
 */
getSessions: async (channel, { depth = 1, count } = {}) => {
  const data = { type: 'interval', channel, depth };
  if (count !== undefined && count !== null) data.count = count;
  return await Fetch.requestData(URLS.general_get, { method: 'post', data });
},

postUpdateAsJsonBeacon(url, data) { // used for several calls, should be more. consider use in Fetch class
  //console.log("api_fetchers.jsp:POST_BEACON called with: "+data);
  
  // Add authToken to data if present (for cross-domain requests)
  if (Fetch.authToken) {
    data.authToken = Fetch.authToken;
  }
  
  // Add contextId to data if present (scopes request to developer's app context)
  if (Fetch.contextId) {
    data.contextId = Fetch.contextId;
  }
  
  // Use fetch with keepalive instead of sendBeacon for better CORS compatibility
  // keepalive ensures the request completes even if the page unloads
  fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
    keepalive: true
  }).catch(err => {
    console.error('postUpdateAsJsonBeacon error:', err);
  });
}

/*
 doQueryThenPopulate(reqUrl, targetSpotSel, clearFirst=true) {
  const targetSpotJQ = $(targetSpotSel);
  if (clearFirst) {targetSpotJQ.html("");}
  fetch(reqUrl)
    .then(response => {
        if (!response.ok) {throw new Error("Network response was not ok " + response.statusText);}
        return response.text();
    })
    .then(html => { targetSpotJQ.append($(html.trim()));})
    .catch(error => {console.error("There was a problem with the fetch operation:", error);});
  },
 getUser(userId, targetSpotSel, look="standard", clearFirst=true) { // used by components ??
  const reqUrl="api/session/getUser_html.jsp?id="+userId+"&look="+look;
  doQueryThenPopulate(reqUrl, targetSpotSel, clearFirst);
},

 getChannel(channelId, targetSpotSel, look="standard", clearFirst=true) { // used anywhere ???
  const reqUrl="api/session/getChannel_html.jsp?id="+channelId+"&look="+look;
  doQueryThenPopulate(reqUrl, targetSpotSel, clearFirst);
},

 getInviteList(targetSpotSel, look="standard", clearFirst=true) {  // not used anywhere ???
  const reqUrl="api/session/getInviteList_html.jsp?look="+look;
  doQueryThenPopulate(reqUrl, targetSpotSel, clearFirst);
},

 getChannelList(targetSpotSel, count=3, look="standard", clearFirst=true) { // not used anywhere ???
  const reqUrl="api/session/getChannelList_html.jsp?count="+count+"&look="+look;
  doQueryThenPopulate(reqUrl, targetSpotSel, clearFirst);
},
*/

}

export default Fetch